import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import styles from './PaymentPlansPage.module.css';

// Visible tiers: Bronze (10), Gümüş (50), Altın (250), Platin (1000), Tycoon (5000), and "Contact Us"
// Each tier can have a "plus" upgrade variant shown as a secondary button
// Demo is no longer a grid card — shown as a trial notice above the grid.
const TIERS = [
  { id: 1, key: 'tier1', emirs: 10, monthlyPrice: 349, plus: { id: 2, key: 'tier2', emirs: 20, monthlyPrice: 379 } },
  { id: 3, key: 'tier3', emirs: 50, monthlyPrice: 449, plus: { id: 4, key: 'tier4', emirs: 100, monthlyPrice: 599 } },
  { id: 5, key: 'tier5', emirs: 250, monthlyPrice: 999, plus: { id: 6, key: 'tier6', emirs: 500, monthlyPrice: 1649 } },
  { id: 7, key: 'tier7', emirs: 1000, monthlyPrice: 1999, plus: { id: 8, key: 'tier8', emirs: 2500, monthlyPrice: 6999 } },
  { id: 9, key: 'tier9', emirs: 5000, monthlyPrice: 13499, plus: { id: 10, key: 'tier10', emirs: 10000, monthlyPrice: 26999 } },
  { id: 11, key: 'tier11', emirs: 0, monthlyPrice: -1 },
];

export function PaymentPlansPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [showDowngrades, setShowDowngrades] = useState(false);

  // SEO for public pricing page
  useSEO({
    title: 'Fiyatlandırma',
    description: 'Polaris fiyatlandırma planları. 1 ücretsiz emirden 10.000 emire kadar esnek paketler. Trendyol, Hepsiburada, Amazon, N11, Pazarama için buybox otomasyonu ve kupon/indirim kontrolü.',
  });

  // For non-authenticated users, show all tiers starting from tier 0
  const currentTier = isAuthenticated ? (user?.membershipTier ?? 0) : -1;

  // Calculate days left until membership expires
  const getDaysLeft = (): number | null => {
    if (!user?.membershipValidTill) return null;
    const expiryDate = new Date(user.membershipValidTill);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysLeft = getDaysLeft();

  // Recommended tier: Platinum (id 7) is the intentional loss-leader, so it stays
  // the recommended pick for visitors and anyone still below it. Users already on
  // or above Platinum get nudged to the next tier up.
  const getRecommendedTier = (): number | null => {
    if (!isAuthenticated) return 7; // Platinum (1000 emirs) for visitors
    if (currentTier < 7) return 7;  // Anyone below Platinum is steered to Platinum
    const nextTier = TIERS.find(t => t.id > currentTier && t.id !== 11);
    return nextTier?.id ?? null;
  };
  const recommendedTier = getRecommendedTier();

  // Filter tiers: show current and higher by default, lower tiers only if showDowngrades is true.
  // A "+" variant being the current plan still counts as the parent card being current
  // (since the + button lives inside the parent card), so include those too.
  // For non-authenticated users, show all tiers
  const visibleTiers = TIERS.filter(tier => {
    if (!isAuthenticated) return true;
    if (tier.id >= currentTier) return true;
    if (tier.plus?.id === currentTier) return true; // parent card hosts the user's + plan
    return showDowngrades;
  });

  // Show the "Alt plana geç" toggle whenever there's at least one paid tier below the user.
  // Bronze (1) is the floor; everyone else has somewhere to go down to.
  const hasLowerTiers = isAuthenticated && currentTier > 1;

  const getTierName = (key: string): string => {
    const names: Record<string, string> = {
      demo: t.paymentPlans.demo,
      tier1: t.paymentPlans.tier1,
      tier2: t.paymentPlans.tier2,
      tier3: t.paymentPlans.tier3,
      tier4: t.paymentPlans.tier4,
      tier5: t.paymentPlans.tier5,
      tier6: t.paymentPlans.tier6,
      tier7: t.paymentPlans.tier7,
      tier8: t.paymentPlans.tier8,
      tier9: t.paymentPlans.tier9,
      tier10: t.paymentPlans.tier10,
      tier11: t.paymentPlans.tier11,
    };
    return names[key] || key;
  };

  const getTierDesc = (key: string): string => {
    const descs: Record<string, string> = {
      demo: t.paymentPlans.demoDesc,
      tier1: t.paymentPlans.tier1Desc,
      tier2: t.paymentPlans.tier2Desc,
      tier3: t.paymentPlans.tier3Desc,
      tier4: t.paymentPlans.tier4Desc,
      tier5: t.paymentPlans.tier5Desc,
      tier6: t.paymentPlans.tier6Desc,
      tier7: t.paymentPlans.tier7Desc,
      tier8: t.paymentPlans.tier8Desc,
      tier9: t.paymentPlans.tier9Desc,
      tier10: t.paymentPlans.tier10Desc,
      tier11: t.paymentPlans.tier11Desc,
    };
    return descs[key] || '';
  };

  const formatPrice = (price: number): string => {
    if (price === 0) return t.paymentPlans.free;
    return `₺${price.toLocaleString('tr-TR')}`;
  };

  // Map a tier id (or "+" variant id) to its palette class. Both halves of a
  // tier family (Silver / Silver+) share the same palette since they belong to
  // the same brand colour. Tier 11 (contact) keeps its own purple styling.
  // - "gradient" returns the metallic 3-stop gradient used on the shimmery "+" buttons.
  // - "flat" returns a single-colour version used on the calmer main CTA buttons.
  const getPaletteClass = (tierId: number, variant: 'gradient' | 'flat'): string => {
    if (variant === 'flat') {
      if (tierId <= 2) return styles.flatBronze;
      if (tierId <= 4) return styles.flatSilver;
      if (tierId <= 6) return styles.flatGold;
      if (tierId <= 8) return styles.flatPlatinum;
      if (tierId <= 10) return styles.flatTycoon;
      return '';
    }
    if (tierId <= 2) return styles.paletteBronze;
    if (tierId <= 4) return styles.paletteSilver;
    if (tierId <= 6) return styles.paletteGold;
    if (tierId <= 8) return styles.palettePlatinum;
    if (tierId <= 10) return styles.paletteTycoon;
    return '';
  };

  const getAnnualPrice = (monthlyPrice: number): number => {
    // 10 months for 12 months of service
    return monthlyPrice * 10;
  };

  const handleBuyClick = (tierKey: string, period: 'monthly' | 'annual') => {
    // Redirect to payment page with tier and period
    // If not authenticated, redirect to login first
    if (!isAuthenticated) {
      // Store intended destination and redirect to login
      navigate(`/login?redirect=/payment?tier=${tierKey}%26period=${period}`);
      return;
    }
    navigate(`/payment?tier=${tierKey}&period=${period}`);
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <Link to={isAuthenticated ? "/" : "/login"} className={styles.backLink}>
            &larr; {isAuthenticated ? t.addOrders.backToDashboard : t.auth.signIn}
          </Link>
          {!isAuthenticated && (
            <button onClick={toggleLanguage} className={styles.langBtn}>
              {language === 'tr' ? 'EN' : 'TR'}
            </button>
          )}
        </div>
        <h1 className={styles.title}>{t.paymentPlans.title}</h1>
        <p className={styles.subtitle}>{t.paymentPlans.subtitle}</p>
      </header>

      {/* Sales Pitch */}
      <section className={styles.pitchSection}>
        <div className={styles.pitchCard}>
          <p>{t.paymentPlans.pitch1}</p>
        </div>
        <div className={styles.pitchCard}>
          <p>{t.paymentPlans.pitch2}</p>
        </div>
        <div className={styles.pitchCard}>
          <p>{t.paymentPlans.pitch3}</p>
        </div>
      </section>

      {/* Billing Toggle */}
      <div className={styles.billingToggle}>
        {hasLowerTiers && (
          <button
            className={`${styles.toggleBtn} ${styles.downgradeToggleBtn} ${showDowngrades ? styles.active : ''}`}
            onClick={() => setShowDowngrades(!showDowngrades)}
          >
            {showDowngrades ? t.paymentPlans.hideDowngrades : t.paymentPlans.showDowngrades}
          </button>
        )}
        <button
          className={`${styles.toggleBtn} ${billingPeriod === 'monthly' ? styles.active : ''}`}
          onClick={() => setBillingPeriod('monthly')}
        >
          {t.paymentPlans.monthly}
        </button>
        <button
          className={`${styles.toggleBtn} ${billingPeriod === 'annual' ? styles.active : ''}`}
          onClick={() => setBillingPeriod('annual')}
        >
          {t.paymentPlans.annual}
          <span className={styles.discountBadge}>{t.paymentPlans.annualDiscount}</span>
        </button>
      </div>

      {/* Demo Trial Notice - only for non-authenticated visitors */}
      {!isAuthenticated && (
        <div className={styles.kdvBox}>
          <p className={styles.kdvNote}>{t.paymentPlans.demoTrialNote}</p>
        </div>
      )}

      {/* Pricing Cards */}
      <div className={styles.pricingGrid}>
        {visibleTiers.map((tier) => {
          const isCurrentPlan = tier.id === currentTier;
          const isPlusCurrentPlan = tier.plus?.id === currentTier;
          const isRecommended = tier.id === recommendedTier;
          const isDowngrade = tier.id < currentTier && !isPlusCurrentPlan;
          // Only the tier immediately below the user's current plan gets the
          // distinct "Alt plana geç" downgrade treatment. Anything further below
          // renders as a normal "Aylık satın al" purchase button. If user is on
          // Bronze (currentTier 1), there is no downgrade target at all.
          const isImmediateDowngradeMain = currentTier > 1 && tier.id === currentTier - 1;
          const isImmediateDowngradePlus = currentTier > 1 && tier.plus?.id === currentTier - 1;
          const price = billingPeriod === 'monthly' ? tier.monthlyPrice : getAnnualPrice(tier.monthlyPrice);

          return (
            <div
              key={tier.id}
              className={`${styles.pricingCard} ${isRecommended ? styles.recommended : ''} ${isCurrentPlan ? styles.currentPlan : ''} ${isDowngrade ? styles.downgrade : ''}`}
            >
              {/* Badges */}
              <div className={styles.cardBadges}>
                {isRecommended && (
                  <span className={styles.recommendedBadge}>{t.paymentPlans.recommended}</span>
                )}
                {(isCurrentPlan || isPlusCurrentPlan) && (
                  <span className={styles.currentBadge}>
                    {t.paymentPlans.currentPlan}{isPlusCurrentPlan ? ` (${getTierName(tier.plus!.key)})` : ''}
                  </span>
                )}
              </div>

              {/* Tier Name */}
              <h3 className={styles.tierName}>
                {getTierName(tier.key)}
                {isCurrentPlan && daysLeft !== null && (
                  <span className={styles.daysLeft}>
                    {t.paymentPlans.daysLeft.replace('{days}', String(daysLeft))}
                  </span>
                )}
              </h3>

              {/* Price */}
              {tier.id !== 11 && (
                <div className={styles.priceContainer}>
                  <span className={styles.price}>{formatPrice(price)}</span>
                  {tier.monthlyPrice > 0 && (
                    <span className={styles.pricePeriod}>
                      {billingPeriod === 'monthly' ? t.paymentPlans.perMonth : t.paymentPlans.perYear}
                    </span>
                  )}
                </div>
              )}

              {/* Emir Count */}
              {tier.id !== 11 && (
                <div className={styles.emirCount}>
                  <span className={styles.emirNumber}>{tier.emirs.toLocaleString('tr-TR')}</span>
                  <span className={styles.emirLabel}>{tier.emirs === 1 ? t.paymentPlans.emirCount : t.paymentPlans.emirs}</span>
                </div>
              )}

              {/* Description */}
              <p className={styles.tierDesc}>{getTierDesc(tier.key)}</p>

              {/* CTA Button — main tier action */}
              <div className={styles.ctaContainer}>
                {isCurrentPlan && tier.monthlyPrice > 0 ? (
                  <button
                    className={styles.renewBtn}
                    onClick={() => handleBuyClick(tier.key, billingPeriod)}
                  >
                    {billingPeriod === 'monthly' ? t.paymentPlans.renewMonthly : t.paymentPlans.renewAnnual}
                  </button>
                ) : tier.id === 11 ? (
                  <a
                    href="mailto:info@polysansoft.com"
                    className={styles.contactBtn}
                  >
                    {t.paymentPlans.contactSales}
                  </a>
                ) : isImmediateDowngradeMain ? (
                  // Only the tier exactly one step below current gets the loud
                  // "Alt plana geç" downgrade button.
                  <button
                    className={styles.downgradeActionBtn}
                    onClick={() => handleBuyClick(tier.key, billingPeriod)}
                  >
                    {t.paymentPlans.downgrade}
                  </button>
                ) : (
                  // Anything else (upgrades, non-immediate downgrades, and the
                  // main tier of a + current plan that's further down) renders
                  // as a normal "Aylık satın al" purchase button — coloured in
                  // the tier's own palette, no shimmer.
                  <button
                    className={`${styles.buyBtn} ${styles.tierBtnBase} ${getPaletteClass(tier.id, 'flat')}`}
                    onClick={() => handleBuyClick(tier.key, billingPeriod)}
                  >
                    {billingPeriod === 'monthly' ? t.paymentPlans.buyMonthly : t.paymentPlans.buyAnnual}
                  </button>
                )}

                {/* Plus variant button — always show when a "+" exists.
                    - If the user holds this + plan, it's a renewal (green).
                    - If it's exactly one step below current, it's the loud downgrade (neutral).
                    - Otherwise it gets the tier palette + shimmer (the shiny version). */}
                {tier.plus && (
                  <button
                    className={
                      isPlusCurrentPlan
                        ? `${styles.plusBtn} ${styles.renewBtn}`
                        : isImmediateDowngradePlus
                          ? `${styles.plusBtn} ${styles.downgradeActionBtn}`
                          : `${styles.plusBtn} ${styles.tierBtnBase} ${styles.shiny} ${getPaletteClass(tier.plus!.id, 'gradient')}`
                    }
                    onClick={() => handleBuyClick(tier.plus!.key, billingPeriod)}
                  >
                    <span className={styles.plusName}>{getTierName(tier.plus!.key)}</span>
                    <span className={styles.plusDetail}>
                      {tier.plus!.emirs.toLocaleString('tr-TR')} {t.paymentPlans.emirs}, {formatPrice(billingPeriod === 'monthly' ? tier.plus!.monthlyPrice : getAnnualPrice(tier.plus!.monthlyPrice))}
                    </span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Section */}
      <section className={styles.infoSection}>
        <div className={styles.infoCard}>
          <h3>{t.paymentPlans.whatIsEmir}</h3>
          <p>{t.paymentPlans.whatIsEmirDesc}</p>
        </div>
        <div className={styles.infoCard}>
          <h3>{t.paymentPlans.annualBenefits}</h3>
          <p>{t.paymentPlans.annualBenefitsDesc}</p>
        </div>
      </section>

      {/* KDV Note */}
      <div className={styles.kdvBox}>
        <p className={styles.kdvNote}>{t.paymentPlans.kdvIncluded}</p>
      </div>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <h2 className={styles.faqTitle}>{t.paymentPlans.faqTitle}</h2>
        <div className={styles.faqGrid}>
          <div className={styles.faqItem}>
            <h4>{t.paymentPlans.faq1Q}</h4>
            <p>{t.paymentPlans.faq1A}</p>
          </div>
          <div className={styles.faqItem}>
            <h4>{t.paymentPlans.faq2Q}</h4>
            <p>{t.paymentPlans.faq2A}</p>
          </div>
          <div className={styles.faqItem}>
            <h4>{t.paymentPlans.faq3Q}</h4>
            <p>{t.paymentPlans.faq3A}</p>
          </div>
        </div>
      </section>

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
        <footer className={styles.legalFooter}>
          <div className={styles.legalFooterContent}>
            <div className={styles.companyInfo}>
              <strong>{t.footer.companyName}</strong>
              <span className={styles.companyType}>{t.footer.companyType}</span>
              <span>{t.footer.address}</span>
              <span>
                <a href={`tel:${t.footer.phone.replace(/\s/g, '')}`}>{t.footer.phone}</a>
                {' | '}
                <a href={`mailto:${t.footer.email}`}>{t.footer.email}</a>
              </span>
            </div>
            <div className={styles.legalLinks}>
              <span className={styles.deliveryBadge}>{t.footer.instantDelivery}</span>
              <Link to="/privacy-policy">{t.footer.privacyPolicy}</Link>
              <Link to="/terms-of-service">{t.footer.termsOfService}</Link>
              <Link to="/return-policy">{t.footer.returnPolicy}</Link>
              <Link to="/cookie-policy">{t.footer.cookiePolicy}</Link>
            </div>
            <div className={styles.copyright}>
              © {new Date().getFullYear()} Polaris - {t.footer.aPolysanProduct}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
